import type { ReactNode } from "react";
import { useInput } from "ra-core";
import { useFieldArray, useWatch } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { MoedaFormInput } from "@/components/admin/moeda-input";
import { FormControl, FormField, FormLabel } from "@/components/admin/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatBRL } from "@/lib/format";

import { calcularAdn, CUSTO_DO_INVENTARIO, idadePelaPlanilha, type Adn } from "./calculoAdn";

/**
 * A ADN de seguro de vida dentro da ficha (reunião de 28/10/2026: "se o cara
 * selecionar seguro de vida, aparecer aquele Excel"). As perguntas e os blocos
 * são os da planilha "ADN - NORTH" do Rodrigo, na mesma ordem, para quem já
 * conduz a entrevista pela planilha não se perder.
 *
 * Gravado em `contacts.details.adn` (JSONB, sem migration), junto do resto da
 * ficha — ver o comentário de `camposDoLead.tsx`. O que a ficha já pergunta em
 * outro passo (nascimento, estado civil, renda, profissão, seguro auto, plano
 * de saúde, altura/peso/fumante do titular) NÃO se repete aqui: duas perguntas
 * para o mesmo dado viram duas respostas.
 *
 * As contas ficam em `calculoAdn.ts`; aqui só a soma de cada bloco aparece
 * embaixo dele, enquanto se preenche.
 */

const A = "details.adn";

export const CampoReais = ({ source, label, helperText }: { source: string; label: string; helperText?: string }) => (
  <MoedaFormInput source={source} label={label} helperText={helperText ?? false} />
);

/** Inteiro curto (anos, idade, nota, %). Vazio grava `null`, não zero. */
export const CampoInteiro = ({ source, label, min, max }: { source: string; label: string; min?: number; max?: number }) => {
  const { id, field } = useInput({ source });
  return (
    <FormField id={id} name={field.name}>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={field.value ?? ""}
          onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
          onBlur={field.onBlur}
        />
      </FormControl>
    </FormField>
  );
};

export const Bloco = ({ titulo, children, rodape }: { titulo: string; children: ReactNode; rodape?: ReactNode }) => (
  <section className="flex flex-col gap-3 rounded-md border p-3">
    <h3 className="text-sm font-semibold">{titulo}</h3>
    {children}
    {rodape && (
      <div className="flex flex-wrap justify-end gap-x-5 gap-y-1 border-t pt-2 text-[13px] tabular-nums text-muted-foreground">
        {rodape}
      </div>
    )}
  </section>
);

export const Soma = ({ rotulo, valor, forte = false }: { rotulo: string; valor: number | null; forte?: boolean }) => (
  <span>
    {rotulo}:{" "}
    <span className={forte ? "font-semibold text-foreground" : "font-medium text-foreground"}>
      {valor == null ? "—" : formatBRL(valor)}
    </span>
  </span>
);

const Dupla = ({ children }: { children: ReactNode }) => <div className="grid gap-3 sm:grid-cols-2">{children}</div>;

/**
 * Lista de linhas (dependentes, etapas de estudo, indicações). `useFieldArray`
 * direto: as linhas são poucas e curtas, e o iterador do admin traz reordenar
 * e "limpar tudo", que aqui só atrapalham.
 */
export const Linhas = ({
  source,
  rotuloAdicionar,
  novo,
  children,
}: {
  source: string;
  rotuloAdicionar: string;
  novo: Record<string, unknown>;
  children: (prefixo: string, indice: number) => ReactNode;
}) => {
  const { fields, append, remove } = useFieldArray({ name: source });
  return (
    <div className="flex flex-col gap-3">
      {fields.map((f, i) => (
        <div key={f.id} className="flex items-start gap-2 rounded-md bg-muted/40 p-2">
          <div className="grid min-w-0 flex-1 gap-3 sm:grid-cols-2">{children(`${source}.${i}`, i)}</div>
          <Button type="button" variant="ghost" size="icon" aria-label="Remover" onClick={() => remove(i)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => append(novo)}>
        <Plus className="mr-1 h-4 w-4" />
        {rotuloAdicionar}
      </Button>
    </div>
  );
};

const IdadeDe = ({ source }: { source: string }) => {
  const idade = idadePelaPlanilha(useWatch({ name: source }));
  return <p className="self-end pb-2 text-[13px] text-muted-foreground">{idade == null ? "" : `${idade} anos`}</p>;
};

/** O resultado das contas com o que está no formulário agora. */
export const useCalculoDaAdn = () => {
  const adn = useWatch({ name: A }) as Adn | undefined;
  const nascimento = useWatch({ name: "details.birth_date" }) as string | undefined;
  const renda = useWatch({ name: "details.monthly_income" }) as number | undefined;
  return calcularAdn(adn, { nascimento, renda });
};

const NOTA = { min: 0, max: 10 };

const REGIMES = ["Comunhão parcial", "Comunhão universal", "Separação total", "Participação final nos aquestos"];
const REDES = ["Pública", "Privada", "Pública e privada"];
const ETAPAS = [
  "Maternal",
  "Fundamental",
  "Médio",
  "Superior",
  "Pós-graduação",
  "Intercâmbio e outros",
];
const CONTRATOS = ["CLT", "PJ", "Autônomo", "Empresário", "Servidor público", "Aposentado"];
const opcoes = (lista: string[]) => lista.map((x) => ({ id: x, name: x }));

const Dependentes = () => (
  <Bloco titulo="Dependentes econômicos">
    <Dupla>
      <DateInput source={`${A}.data_consultoria`} label="Data da consultoria" helperText={false} />
      <SelectInput source={`${A}.regime_bens`} label="Regime de bens" choices={opcoes(REGIMES)} helperText={false} />
      <DateInput source={`${A}.conjuge_nascimento`} label="Nascimento do cônjuge" helperText={false} />
      <IdadeDe source={`${A}.conjuge_nascimento`} />
    </Dupla>
    <Linhas source={`${A}.dependentes`} rotuloAdicionar="Dependente" novo={{ nome: "", nascimento: null, parentesco: "" }}>
      {(p) => (
        <>
          <TextInput source={`${p}.nome`} label="Nome" helperText={false} />
          <TextInput source={`${p}.parentesco`} label="Parentesco" helperText={false} />
          <DateInput source={`${p}.nascimento`} label="Nascimento" helperText={false} />
          <IdadeDe source={`${p}.nascimento`} />
        </>
      )}
    </Linhas>
  </Bloco>
);

const Educacao = () => {
  const r = useCalculoDaAdn();
  return (
    <Bloco
      titulo="Educação dos filhos"
      rodape={
        <>
          <Soma rotulo="Total" valor={r.educacao.total} />
          <Soma rotulo="Saldo" valor={r.educacao.saldo} forte />
        </>
      }
    >
      <Dupla>
        <CampoInteiro source={`${A}.educacao_nota`} label="Importância (0 a 10)" {...NOTA} />
        <SelectInput source={`${A}.educacao_rede`} label="Rede de ensino" choices={opcoes(REDES)} helperText={false} />
      </Dupla>
      <Linhas
        source={`${A}.educacao`}
        rotuloAdicionar="Etapa de estudo"
        novo={{ filho: "", etapa: null, custo_mensal: 0, anos: null }}
      >
        {(p) => (
          <>
            <TextInput source={`${p}.filho`} label="Filho" helperText={false} />
            <SelectInput source={`${p}.etapa`} label="Etapa" choices={opcoes(ETAPAS)} helperText={false} />
            <CampoReais source={`${p}.custo_mensal`} label="Custo mensal" />
            <CampoInteiro source={`${p}.anos`} label="Anos restantes" min={0} />
          </>
        )}
      </Linhas>
      <CampoReais source={`${A}.educacao_reserva`} label="Reserva financeira para educação" />
    </Bloco>
  );
};

const PadraoDeVida = () => {
  const r = useCalculoDaAdn();
  return (
    <Bloco
      titulo="Padrão de vida"
      rodape={
        <>
          <Soma rotulo="Gasto mensal" valor={r.gastoMensal} />
          <Soma rotulo="Invalidez" valor={r.invalidez.diferenca} forte />
          <Soma rotulo="Doenças graves" valor={r.doencas.diferenca} forte />
        </>
      }
    >
      <CampoInteiro source={`${A}.padrao_nota`} label="Importância (0 a 10)" {...NOTA} />
      <Dupla>
        <CampoReais
          source={`${A}.gastos.fixos_casa`}
          label="Gastos fixos: casa"
          helperText="Moradia, condomínio, água, luz, telefone, alimentação, IPVA e IPTU."
        />
        <CampoReais
          source={`${A}.gastos.fixos_saude`}
          label="Gastos fixos: saúde e mercado"
          helperText="Farmácia, plano de saúde, mercado, pensão, beleza e médico."
        />
        <CampoReais
          source={`${A}.gastos.outros`}
          label="Outros gastos"
          helperText="Empregada, pet, academia, doação, cartão e similares."
        />
        <CampoReais
          source={`${A}.gastos.lazer`}
          label="Lazer"
          helperText="Passeios, restaurantes, vestuário e combustível."
        />
        <CampoInteiro source={`${A}.participacao_titular`} label="Participação do titular na renda (%)" min={0} max={100} />
        <p className="self-end pb-2 text-[13px] text-muted-foreground">
          Cônjuge: {Math.round(100 - r.participacaoTitular)}%
        </p>
        <CampoInteiro source={`${A}.aposentadoria_titular`} label="Aposentadoria do titular (idade)" min={0} />
        <CampoInteiro source={`${A}.aposentadoria_conjuge`} label="Aposentadoria do cônjuge (idade)" min={0} />
        <CampoReais source={`${A}.reservado_invalidez`} label="Montante reservado: invalidez" />
        <CampoReais source={`${A}.reservado_doencas`} label="Montante reservado: doenças graves" />
      </Dupla>
    </Bloco>
  );
};

const Inventario = () => {
  const r = useCalculoDaAdn();
  return (
    <Bloco
      titulo="Inventário do patrimônio"
      rodape={
        <>
          <Soma rotulo="Patrimônio" valor={r.inventario.base} />
          <Soma rotulo={`Custo do inventário (${CUSTO_DO_INVENTARIO * 100}%)`} valor={r.inventario.custo} forte />
        </>
      }
    >
      <Dupla>
        <CampoReais source={`${A}.imoveis`} label="Imóveis" />
        <CampoReais source={`${A}.veiculos`} label="Veículos" />
        <TextInput source={`${A}.veiculos_modelo`} label="Modelo dos veículos" helperText={false} />
        <span />
        <CampoReais source={`${A}.previdencia_mensal`} label="Previdência: aporte mensal" />
        <CampoReais
          source={`${A}.previdencia_anual`}
          label="Previdência: aporte anual"
          helperText="Fora da base do inventário."
        />
        <CampoReais source={`${A}.reserva_financeira`} label="Reserva financeira" helperText="Fundos, ações e reservas líquidas." />
        <TextInput source={`${A}.reserva_objetivo`} label="Objetivo da reserva" helperText={false} />
      </Dupla>
    </Bloco>
  );
};

const DIVIDAS = [
  ["auto", "Financiamento de veículo"],
  ["imobiliario", "Financiamento imobiliário"],
  ["pessoais", "Empréstimos pessoais"],
  ["outros", "Outras dívidas"],
] as const;

const Dividas = () => {
  const r = useCalculoDaAdn();
  return (
    <Bloco
      titulo="Dívidas"
      rodape={
        <>
          <Soma rotulo="Parcelas por mês" valor={r.dividas.parcelas} />
          <Soma rotulo="Dívida total" valor={r.dividas.total} forte />
        </>
      }
    >
      <Dupla>
        {DIVIDAS.map(([chave, rotulo]) => (
          <div key={chave} className="contents">
            <CampoReais source={`${A}.dividas.${chave}.total`} label={`${rotulo}: saldo`} />
            <CampoReais source={`${A}.dividas.${chave}.parcela`} label={`${rotulo}: parcela`} />
          </div>
        ))}
      </Dupla>
    </Bloco>
  );
};

const ProtecaoERenda = () => {
  const r = useCalculoDaAdn();
  return (
    <Bloco
      titulo="Proteção financeira e renda"
      rodape={
        <>
          <Soma rotulo="Receita líquida" valor={r.renda.receita} />
          <Soma rotulo="Despesas" valor={r.renda.despesas} />
          <Soma rotulo="Saldo mensal" valor={r.renda.saldo} forte />
        </>
      }
    >
      <Dupla>
        <CampoReais source={`${A}.seguro_auto_anual`} label="Seguro auto: custo anual" />
        <span />
        <CampoReais source={`${A}.seguro_vida_anual`} label="Seguro de vida: custo anual" />
        <TextInput source={`${A}.seguro_vida_seguradora`} label="Seguradora do seguro de vida" helperText={false} />
        <SelectInput source={`${A}.contrato_titular`} label="Contrato de trabalho do titular" choices={opcoes(CONTRATOS)} helperText={false} />
        <TextInput source={`${A}.inss_titular`} label="INSS do titular (faixa)" helperText={false} />
        <CampoReais source={`${A}.renda_conjuge`} label="Renda líquida do cônjuge" />
        <TextInput source={`${A}.profissao_conjuge`} label="Profissão do cônjuge" helperText={false} />
        <SelectInput source={`${A}.contrato_conjuge`} label="Contrato de trabalho do cônjuge" choices={opcoes(CONTRATOS)} helperText={false} />
        <TextInput source={`${A}.inss_conjuge`} label="INSS do cônjuge (faixa)" helperText={false} />
      </Dupla>
      <p className="text-[13px] text-muted-foreground">
        Renda e profissão do titular, seguradora e vencimento do seguro auto: passos Trabalho e Seguros.
      </p>
    </Bloco>
  );
};

const Riscos = () => (
  <Bloco titulo="Análise de riscos">
    <Dupla>
      <TextInput source={`${A}.riscos.hobby`} label="Hobby de risco" helperText="Mergulho, escalada, paraquedismo e similares." />
      <TextInput source={`${A}.riscos.esporte`} label="Esporte praticado" helperText={false} />
      <TextInput source={`${A}.riscos.medicamento`} label="Medicamento de uso contínuo" helperText={false} />
      <TextInput source={`${A}.riscos.cirurgia`} label="Cirurgia complexa" helperText={false} />
      <TextInput
        source={`${A}.riscos.doenca`}
        label="Doença crônica ou grave"
        helperText="Câncer, diabetes, hipertensão, infarto, AVC, insuficiência renal e similares."
      />
      <TextInput source={`${A}.riscos.bebida`} label="Bebida alcoólica (consumo)" helperText={false} />
      <SelectInput
        source={`${A}.riscos.vacina_covid`}
        label="Vacinação completa contra COVID-19"
        choices={opcoes(["Sim", "Não"])}
        helperText={false}
      />
      <BooleanInput source={`${A}.riscos.moto`} label="Anda de moto" helperText={false} />
    </Dupla>
    <h4 className="pt-1 text-[13px] font-medium text-muted-foreground">Cônjuge</h4>
    <Dupla>
      <TextInput source={`${A}.riscos.conjuge_saude`} label="Saúde do cônjuge" helperText={false} />
      <TextInput source={`${A}.riscos.conjuge_medicamento`} label="Medicamento de uso contínuo" helperText={false} />
      <TextInput source={`${A}.riscos.conjuge_doenca`} label="Doença crônica ou grave" helperText={false} />
      <TextInput source={`${A}.riscos.conjuge_bebida`} label="Bebida alcoólica (consumo)" helperText={false} />
      <TextInput source={`${A}.riscos.conjuge_altura`} label="Altura (m)" helperText={false} />
      <TextInput source={`${A}.riscos.conjuge_peso`} label="Peso (kg)" helperText={false} />
      <BooleanInput source={`${A}.riscos.conjuge_fuma`} label="Cônjuge fumante" helperText={false} />
    </Dupla>
    <p className="text-[13px] text-muted-foreground">Altura, peso e fumante do titular: passo Seguros.</p>
  </Bloco>
);

/** O passo "ADN" inteiro: o mapeamento, bloco a bloco como na planilha. */
export const CamposDaAdn = () => (
  <>
    <Dependentes />
    <Educacao />
    <PadraoDeVida />
    <Inventario />
    <Dividas />
    <ProtecaoERenda />
    <Riscos />
  </>
);
